import { RequestHandler, rest } from 'msw';
import NodeCache from 'node-cache';
import { v4 as uuidv4 } from 'uuid';
import {
  NewProject,
  Project,
  PagedResponse,
  EditProject,
  Standards,
  SortBy,
  SortDirection,
  SummerExternalColor,
} from '../types/domainTypes';
import mockStandards from './data/mockStandards.json';
import mockTemperatureData from './data/mockTemperatureData.json';
import { mockProjects } from './data/mockProjects';

const mockDataCache = new NodeCache();
const key = 'Test';
mockDataCache.set(key, mockProjects);

export const mockData = {
  getTemperatureData(): SummerExternalColor[] {
    const tempertaureData = mockTemperatureData as SummerExternalColor[];
    return tempertaureData;
  },
  getStandards(): Standards[] {
    const standards = mockStandards as Standards[];
    return standards;
  },
  getAllProjects(): Project[] {
    const projects = mockDataCache.get<Project[]>(key) as Project[];
    return projects;
  },
  getSortedProjects(sortBy: SortBy, sortDirection: SortDirection): Project[] {
    const projects = mockDataCache.get<Project[]>(key) as Project[];
    let sortedProjects: Project[];
    switch (sortBy) {
      case SortBy.LastModified:
        sortedProjects = projects.sort((a, b) => (a.modifiedOn > b.modifiedOn ? 1 : -1));
        break;
      case SortBy.DateCreated:
        sortedProjects = projects.sort((a, b) => (a.createdOn > b.createdOn ? 1 : -1));
        break;
      case SortBy.ProjectName:
        sortedProjects = projects.sort((a, b) => (a.name > b.name ? 1 : -1));
        break;
      case SortBy.EngineerName:
        sortedProjects = projects.sort((a, b) => (a.engineer > b.engineer ? 1 : -1));
        break;
    }

    return sortDirection === SortDirection.Ascending ? sortedProjects : sortedProjects.reverse();
  },
  getProjectsByPage(pageNumber: number, pageSize: number, sortBy: SortBy, sortDirection: SortDirection): Project[] {
    const pageIndex = pageSize * (pageNumber - 1);
    const projects = this.getSortedProjects(sortBy, sortDirection);
    return projects?.slice(pageIndex, pageIndex + pageSize);
  },
  getProjectById(id: string): Project | undefined {
    const projects = mockDataCache.get(key) as Project[];
    const project = projects.find((cachedProject: Project) => cachedProject.id === id);
    return project;
  },
  saveProject(project: NewProject | EditProject): Project | undefined {
    let projects = this.getAllProjects();
    let savedProject: Project;

    if ('id' in project) {
      const projectToBeUpdated = projects.find((proj) => proj.id === project.id);
      if (projectToBeUpdated === undefined) return undefined;
      savedProject = {
        ...project,
        status: project.status,
        createdBy: projectToBeUpdated.createdBy,
        createdOn: projectToBeUpdated.createdOn,
        createdByName: projectToBeUpdated.createdByName,
        modifiedOn: new Date().toISOString(),
        modifiedBy: 'John',
        modifiedByName: 'John',
        calculations: projectToBeUpdated.calculations,
      };
    } else {
      savedProject = {
        ...project,
        id: uuidv4(),
        modifiedOn: new Date().toISOString(),
        modifiedBy: 'John',
        modifiedByName: 'John',
        createdBy: 'John',
        createdByName: 'John',
        createdOn: new Date().toISOString(),
        calculations: [],
      };
    }
    projects = projects.filter((proj) => proj.id !== savedProject.id);
    const projectsWithNewProject = [savedProject, ...projects];
    mockDataCache.set(key, projectsWithNewProject);

    return savedProject;
  },
};

// TODO Can types be improved from any any?

export const getAppApiHandlers = (apiUrl: string): Array<RequestHandler> => {
  return [
    rest.get(`${apiUrl}/Project`, (request, response, context) => {
      const pageSize = parseInt(request.url.searchParams.get('pageSize') as string);
      const pageNumber = parseInt(request.url.searchParams.get('pageNumber') as string);
      const sortBy = request.url.searchParams.get('sortBy') as SortBy;
      const sortDirection = request.url.searchParams.get('sortDirection') as SortDirection;

      const allProjects = mockData.getAllProjects() as Project[];
      const totalCount = allProjects.length;

      const projects = mockData.getProjectsByPage(pageNumber, pageSize, sortBy, sortDirection) as Project[];

      const pagedResponse: PagedResponse = {
        pageSize: pageSize,
        pageNumber: pageNumber,
        total: totalCount,
        totalPages: Math.ceil(totalCount / pageSize),
        data: projects,
      };

      return response(context.status(200), context.json(pagedResponse));
    }),
    rest.get(`${apiUrl}/Project/:projectId`, (request, response, context) => {
      const { projectId } = request.params;
      const project = mockData.getProjectById(projectId);
      return response(context.status(200), context.json(project));
    }),
    rest.get(`${apiUrl}/Project/:projectId/calculation/:calculationId`, (request, response, context) => {
      return response(context.status(200));
    }),
    rest.post(`${apiUrl}/Project/`, (request, response, context) => {
      const project = request.body as NewProject | EditProject;
      const savedProject = mockData.saveProject(project);
      if (savedProject === undefined) {
        return response(context.status(404), context.json('Project not found'));
      }
      return response(context.status(200), context.json(savedProject));
    }),
    rest.get(`${apiUrl}/Standards`, (request, response, context) => {
      const standards = mockData.getStandards();
      return response(context.status(200), context.json(standards));
    }),
    rest.get(`${apiUrl}/TemperatureData`, (request, response, context) => {
      const temperatureData = mockData.getTemperatureData();
      return response(context.status(200), context.json(temperatureData));
    }),
    rest.post(`${apiUrl}/Project/:projectId/standard`, (request, response, context) => {
      return response(context.status(200));
    }),
    rest.post(`${apiUrl}/project/:projectId/calculation`, (request, response, context) => {
      return response(context.status(200));
    }),
    rest.post(`${apiUrl}/project/:projectId/calculation/:calculationId/temperature`, (request, response, context) => {
      return response(context.status(200));
    }),
    rest.post(`${apiUrl}/project/:projectId/calculation/:calculationId/spansandloads`, (request, response, context) => {
      return response(context.status(200));
    }),
    rest.get(`${apiUrl}/supportwidths`, (request, response, context) => {
      return response(context.status(200));
    }),
  ];
};

export const getHandlers = (apiUrl: string): Array<RequestHandler> => {
  return getAppApiHandlers(apiUrl);
};
